// Client-side photo compression for the clinic workspace (Phase 2e.1).
//
// The clinic app runs in a browser on a shared workstation — iPhone-size
// camera JPEGs are overkill for a "wound dressing" snapshot and bloat the
// Storage bucket. We downscale to 1600px on the long edge and re-encode as
// JPEG at q=0.82, which gives ~80–200KB files for the typical procedure
// photo. Keeps the signed PUT round-trip snappy over clinic LTE.
//
// Not shared with the mobile apps — RN uses lib/photo.ts in apps/mobile
// which goes through expo-image-manipulator (OS-native). This module is
// browser-only (canvas + createImageBitmap).

const MAX_LONG_EDGE = 1600;
const JPEG_QUALITY = 0.82;
const OUTPUT_MIME = 'image/jpeg';

export interface CompressedPhoto {
    blob: Blob;
    contentType: string; // always 'image/jpeg' for the compressed output
    width: number;
    height: number;
}

export async function compressPhoto(file: File): Promise<CompressedPhoto> {
    if (!file.type.startsWith('image/')) {
        throw new Error('Not an image file.');
    }
    // createImageBitmap honours EXIF orientation on modern Chromium/Safari,
    // so we don't need a separate orientation step. Fall back to <img>
    // decoding on browsers without the option — the fallback is tolerable
    // because clinic workstations run modern Chrome/Edge.
    let bitmap: ImageBitmap;
    try {
        bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
        bitmap = await decodeViaImgElement(file);
    }

    const srcW = bitmap.width;
    const srcH = bitmap.height;
    const long = Math.max(srcW, srcH);
    const scale = long > MAX_LONG_EDGE ? MAX_LONG_EDGE / long : 1;
    const dstW = Math.max(1, Math.round(srcW * scale));
    const dstH = Math.max(1, Math.round(srcH * scale));

    const canvas = document.createElement('canvas');
    canvas.width = dstW;
    canvas.height = dstH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D unavailable.');
    ctx.drawImage(bitmap, 0, 0, dstW, dstH);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, OUTPUT_MIME, JPEG_QUALITY),
    );
    if (!blob) throw new Error('Compression failed.');
    return { blob, contentType: OUTPUT_MIME, width: dstW, height: dstH };
}

async function decodeViaImgElement(file: File): Promise<ImageBitmap> {
    const url = URL.createObjectURL(file);
    try {
        const img = new Image();
        img.decoding = 'async';
        img.src = url;
        await img.decode();
        // Round-trip through createImageBitmap so the rest of the helper
        // can treat the result uniformly.
        return await createImageBitmap(img);
    } finally {
        URL.revokeObjectURL(url);
    }
}

// PUT the compressed blob to the signed Supabase upload URL. Returns on
// 2xx and throws otherwise — the caller surfaces the error up to the UI.
export async function putPhotoToSignedUrl(signedUrl: string, blob: Blob): Promise<void> {
    const res = await fetch(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': blob.type || 'image/jpeg' },
        body: blob,
    });
    if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(`Upload failed (${res.status}). ${detail.slice(0, 200)}`);
    }
}
